import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';

import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';

import { TradeBenefitsOverviewSection } from './TradeBenefitsOverviewSection.tsx';

import { BASE_IMAGE_URL, NET_PRICE_PERCENTAGE } from '../../tradeBenefitsConstants.ts';

import styles from './TradeBenefitsOverviewSections.scss';
import dibsCss from 'dibs-css';

const ItalicTitle: FC<{ children: ReactNode }> = ({ children }) => (
    <span className={dibsCss.sassyFontHeaderMediumItalic}>{children}</span>
);

const sections = [
    {
        key: 'pricing',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsOverview.pricing.title"
                defaultMessage="Exclusive <italic>Pricing & Tools</italic>"
                values={{
                    italic: content => <ItalicTitle>{content}</ItalicTitle>,
                }}
            />
        ),
        imagePath: `${BASE_IMAGE_URL}/exclusive_pricing_benefits_tile.jpg`,
        items: [
            {
                id: 'net-price',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.pricing.netPrice"
                        defaultMessage="Net Price on Furniture and Art"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.pricing.netPrice.content"
                        defaultMessage="Enjoy up to {percentage} in net price discounts on unique pieces from our exceptional supply of contemporary, vintage, and antique furniture and art."
                        values={{
                            percentage: (
                                <FormattedNumber
                                    style="percent"
                                    value={NET_PRICE_PERCENTAGE / 100}
                                />
                            ),
                        }}
                    />
                ),
            },
            {
                id: 'tax-exempt',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.pricing.taxExempt"
                        defaultMessage="Tax Exempt Purchasing"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.pricing.taxExempt.content"
                        defaultMessage="Enjoy tax free purchasing by uploading your verified tax resale certificates to your Trade 1st account. Once you upload your certificate, all account users will have access to tax free shopping."
                    />
                ),
            },
            {
                id: 'hold-requests',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.pricing.holdRequests"
                        defaultMessage="Unlimited Hold Requests"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.pricing.holdRequests.content"
                        defaultMessage="Request unlimited holds on eligible pieces, giving your firm the flexibility to reserver items while final approvals are made with your team or client."
                    />
                ),
            },
        ],
    },
    {
        key: 'service',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsOverview.service.title"
                defaultMessage="Exceptional <italic>Service</italic>"
                values={{
                    italic: content => <ItalicTitle>{content}</ItalicTitle>,
                }}
            />
        ),
        imagePath: `${BASE_IMAGE_URL}/exceptional_service_benefits_tile.jpg`,
        items: [
            {
                id: 'customer-support',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.service.customerSupport"
                        defaultMessage="Customer Support"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.service.customerSupport.content"
                        defaultMessage="If you have questions or run into an issue with an order, our Customer Support team offers responsive and reliable assistance so you can quickly get the help you need and keep your shopping experience running smoothly."
                    />
                ),
            },
        ],
    },
    {
        key: 'purchaseProtection',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsOverview.purchaseProtection.title"
                defaultMessage="Industry Leading <italic>Purchase Protection</italic>"
                values={{
                    italic: content => <ItalicTitle>{content}</ItalicTitle>,
                }}
            />
        ),
        imagePath: `${BASE_IMAGE_URL}/purchase_protection_benefits_tile.jpg`,
        items: [
            {
                id: 'authenticity-guarantee',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.authenticity"
                        defaultMessage="Authenticity Guarantee"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.authenticity.content"
                        defaultMessage="We understand the importance of quality, so if any item is found to be inauthentic within one year, we will provide a full refund."
                    />
                ),
            },
            {
                id: 'price-match',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.priceMatch"
                        defaultMessage="Price Match Guarantee"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.priceMatch.content"
                        defaultMessage="We want to ensure you always get the best value for your client. If the same seller has the item listed lower elsewhere, we’ll match it."
                    />
                ),
            },
            {
                id: 'return-policy',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.returnPolicy"
                        defaultMessage="Expanded Return Policy"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.returnPolicy.content"
                        defaultMessage="Enjoy a minimum of 14 days to return an item plus waived restocking fees — even if the seller’s standard policy doesn’t allow returns."
                    />
                ),
            },
            {
                id: 'money-back-guarantee',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.moneyBack"
                        defaultMessage="Money-Back Guarantee"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.purchaseProtection.moneyBack.content"
                        defaultMessage="If your item is not as described, is damaged in transit, or does not arrive, contact us within 30 days and you may be eligible for a refund. We’ll work with you to make it right to keep your project on track."
                    />
                ),
            },
        ],
    },
    {
        key: 'publicity',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsOverview.publicity.title"
                defaultMessage="Publicity <italic>Opportunities</italic>"
                values={{
                    italic: content => <ItalicTitle>{content}</ItalicTitle>,
                }}
            />
        ),
        imagePath: `${BASE_IMAGE_URL}/publicity_opportunities_benefits_tile.jpg`,
        items: [
            {
                id: 'design-directory',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.publicity.designDirectory"
                        defaultMessage="Firm Portfolio in 1stDibs' Design Directory"
                    />
                ),
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsOverview.publicity.designDirectory.content"
                        defaultMessage="Create a Firm Profile and share your portfolio of projects in the 1stDibs Design Directory. Having a 1stDibs portfolio is a great way to increase your exposure among our community of over 4.3M registered users. Plus, it gets your work in front of our internal editorial, social and marketing teams."
                    />
                ),
            },
        ],
    },
];

const TradeBenefitsOverviewSections: FC = () => {
    return (
        <div className={classnames(styles.grid, dibsCss.grid, dibsCss.gapMedium)}>
            {sections.map(({ key, title, items, imagePath }) => (
                <TradeBenefitsOverviewSection
                    key={key}
                    title={title}
                    items={items}
                    imagePath={imagePath}
                />
            ))}
        </div>
    );
};

export { TradeBenefitsOverviewSections };
